import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { saveAs } from 'file-saver';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { IGenericServiceResponse } from '../interfaces/IServiceResponse';
import { IDischargeReportPreviewData } from '../interfaces/DischargeReport/IDischargeReportPreview';
import { IDischargeReportPreviewRequest } from '../interfaces/DischargeReport/IDischargeReportPreviewRequest';
import {
  IDischargeReportIssueRequest,
  IIssuedDischargeReportData,
} from '../interfaces/DischargeReport/IDischargeReportIssue';
import {
  IDischargeReportErrorResponse,
  IDischargeReportValidationIssue,
} from '../interfaces/DischargeReport/IDischargeReportValidation';
import { LocaleService } from './locale.service';
import { PatientService } from './patient.service';
import { SwalToastService } from './swal.service';

export interface IDischargeReportPreviewError {
  status: 'ineligible' | 'error';
  message?: string;
  issues: IDischargeReportValidationIssue[];
}

@Injectable({ providedIn: 'root' })
export class DischargeReportService {
  public isOpenSub = new BehaviorSubject<boolean>(false);
  public isLoadingSub = new BehaviorSubject<boolean>(false);
  public dataSub = new BehaviorSubject<IDischargeReportPreviewData | undefined>(
    undefined
  );
  public errorSub = new BehaviorSubject<IDischargeReportPreviewError | undefined>(
    undefined
  );
  public isDownloadingPdfSub = new BehaviorSubject<boolean>(false);

  private currentEpisodeCareId?: number;

  constructor(
    private readonly _http: HttpClient,
    private readonly localeService: LocaleService,
    private readonly patientService: PatientService,
    private readonly swalToastService: SwalToastService
  ) {}

  buildPreviewUrl(patientId: string, episodeCareId: number): string {
    return `${environment.BACKEND_URL}DischargeReport/${patientId}/${episodeCareId}/Preview`;
  }

  buildPreviewRequest(): IDischargeReportPreviewRequest {
    return {
      language: this.localeService.getCurrentLocale() || 'en',
    };
  }

  preview(
    patientId: string,
    episodeCareId: number
  ): Observable<IGenericServiceResponse<IDischargeReportPreviewData>> {
    const url = this.buildPreviewUrl(patientId, episodeCareId);
    const body = this.buildPreviewRequest();
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this._http.post<IGenericServiceResponse<IDischargeReportPreviewData>>(
      url,
      body,
      { headers }
    );
  }

  open(episodeCareId: number): void {
    if (this.isLoadingSub.value) {
      return;
    }

    this.currentEpisodeCareId = episodeCareId;
    this.isOpenSub.next(true);
    this.isLoadingSub.next(true);
    this.dataSub.next(undefined);
    this.errorSub.next(undefined);

    const patientId = this.patientService.patientIDsignal();

    this.preview(patientId, episodeCareId).subscribe({
      next: (response) => {
        this.dataSub.next(response?.data);
        this.isLoadingSub.next(false);
      },
      error: (err: HttpErrorResponse) => {
        const body = err?.error as IDischargeReportErrorResponse | null;
        const isIneligible = err.status === 409;

        this.errorSub.next({
          status: isIneligible ? 'ineligible' : 'error',
          message: body?.message,
          issues: body?.data?.issues ?? [],
        });
        this.isLoadingSub.next(false);

        this.swalToastService.toastError(
          isIneligible
            ? 'This Episode of Care is not eligible for a discharge report'
            : 'Unable to generate the discharge report preview'
        );
        console.error('Error fetching discharge report preview:', err);
      },
    });
  }

  close(): void {
    this.isOpenSub.next(false);
    this.isLoadingSub.next(false);
    this.dataSub.next(undefined);
    this.errorSub.next(undefined);
    this.currentEpisodeCareId = undefined;
  }

  applyIssuedResult(result: IIssuedDischargeReportData): void {
    const current = this.dataSub.value;
    if (!current) {
      return;
    }

    this.dataSub.next({
      ...current,
      issuedReport: {
        exists: true,
        reportId: result.reportId,
        issuedAt: result.issuedAt,
      },
      validation: {
        ...current.validation,
        canIssue: false,
      },
    });
  }

  buildIssueUrl(patientId: string, episodeCareId: number): string {
    return `${environment.BACKEND_URL}DischargeReport/${patientId}/${episodeCareId}/Issue`;
  }

  issue(
    request: IDischargeReportIssueRequest,
    idempotencyKey: string
  ): Observable<IGenericServiceResponse<IIssuedDischargeReportData>> {
    const patientId = this.patientService.patientIDsignal();
    const url = this.buildIssueUrl(patientId, this.currentEpisodeCareId!);
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Idempotency-Key': idempotencyKey,
    });

    return this._http.post<IGenericServiceResponse<IIssuedDischargeReportData>>(
      url,
      request,
      { headers }
    );
  }

  buildDownloadPdfUrl(patientId: string, reportId: number): string {
    return `${environment.BACKEND_URL}DischargeReport/${patientId}/${reportId}/Pdf`;
  }

  downloadPdf(reportId: number): void {
    if (this.isDownloadingPdfSub.value) {
      return;
    }

    const patientId = this.patientService.patientIDsignal();
    const url = this.buildDownloadPdfUrl(patientId, reportId);

    this.isDownloadingPdfSub.next(true);

    this._http
      .get(url, { observe: 'response', responseType: 'blob' })
      .subscribe({
        next: (response: HttpResponse<Blob>) => {
          this.isDownloadingPdfSub.next(false);

          const contentDisposition = response.headers.get(
            'Content-Disposition'
          );
          let fileName = 'DischargeReport.pdf';
          if (contentDisposition) {
            const matches = contentDisposition.match(
              /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
            );
            if (matches && matches[1]) {
              fileName = matches[1].replace(/['"]/g, '');
            }
          }

          const contentType =
            response.headers.get('Content-Type') ?? 'application/pdf';
          const file = new Blob([response.body!], { type: contentType });
          saveAs(file, fileName);
        },
        error: (err: HttpErrorResponse) => {
          this.isDownloadingPdfSub.next(false);
          this.swalToastService.toastError(
            'Unable to download the discharge report PDF'
          );
          console.error('Error downloading discharge report PDF:', err);
        },
      });
  }

  downloadAttachment(url: string): Observable<HttpResponse<Blob>> {
    const path = url.startsWith('/') ? url.slice(1) : url;
    return this._http.get(`${environment.BACKEND_URL}${path}`, {
      responseType: 'blob',
      observe: 'response',
    });
  }
}